import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Gift-card lookup + balance display. The actual redemption happens in
 * ``handleProcessPayment`` (settle-from-balance path). This panel just
 * gates the Pay button by exposing lookup state so the parent can
 * validate ``current_balance >= total``.
 */
const GiftCardPanel = ({
  total,
  gcCode, setGcCode,
  gcLookup, setGcLookup,
  gcLookupErr, setGcLookupErr,
  gcLookingUp, setGcLookingUp,
}) => (
  <div className="space-y-3">
    <div
      className="rounded-md border border-dashed border-primary/40 bg-primary/5 px-3 py-2 text-xs text-primary"
      data-testid="gift-card-panel"
    >
      Scan, swipe or type the gift card code, then look up to see the balance.
    </div>
    <div className="space-y-1">
      <Label>Card code</Label>
      <div className="flex gap-2">
        <Input
          value={gcCode}
          onChange={(e) => { setGcCode(e.target.value); setGcLookup(null); setGcLookupErr(""); }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              const el = document.querySelector('[data-testid="gc-payment-lookup-btn"]');
              el?.click();
            }
          }}
          placeholder="Scan / type / swipe..."
          className="font-mono"
          data-testid="gift-card-code"
          autoFocus
        />
        <Button
          variant="secondary"
          disabled={!gcCode.trim() || gcLookingUp}
          onClick={async () => {
            setGcLookingUp(true);
            setGcLookupErr("");
            try {
              const { data } = await axios.get(`${API}/gift-cards/${encodeURIComponent(gcCode.trim())}`);
              setGcLookup(data);
            } catch (err) {
              setGcLookup(null);
              setGcLookupErr(err.response?.data?.detail || "Not found");
            } finally {
              setGcLookingUp(false);
            }
          }}
          data-testid="gc-payment-lookup-btn"
        >
          {gcLookingUp ? "…" : "Look up"}
        </Button>
      </div>
      {gcLookupErr && (
        <p className="text-xs text-destructive" data-testid="gc-payment-error">{gcLookupErr}</p>
      )}
    </div>
    {gcLookup && (
      <div className="rounded-lg border p-3 space-y-2" data-testid="gc-payment-detail">
        <div className="flex items-center justify-between">
          <p className="text-xs text-muted-foreground">
            Card {`••••${(gcLookup.code || "").slice(-4)}`}
          </p>
          <span className="text-xs capitalize">{gcLookup.status}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">Balance</span>
          <span className="font-heading text-xl" data-testid="gc-payment-balance">
            ${(gcLookup.current_balance ?? 0).toFixed(2)}
          </span>
        </div>
        {gcLookup.current_balance < total ? (
          <div
            className="rounded bg-amber-50 dark:bg-amber-950/30 text-amber-700 dark:text-amber-300 text-xs p-2"
            data-testid="gc-payment-shortfall"
          >
            Card can&apos;t cover the ${total.toFixed(2)} total.
            Short by <strong>${(total - gcLookup.current_balance).toFixed(2)}</strong>.
            Redeem the balance, then charge the remainder to another tender.
          </div>
        ) : (
          <p className="text-xs text-muted-foreground">
            Press Pay to redeem ${total.toFixed(2)} — leaves ${(gcLookup.current_balance - total).toFixed(2)} on the card.
          </p>
        )}
      </div>
    )}
  </div>
);

export default GiftCardPanel;
